import { Link } from "react-router-dom";
import LandingNav from "@/pages/home/components/LandingNav";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-[#0A0B0F] text-white font-sans">
      <LandingNav />

      <section className="pt-28 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-[#C8A96E] text-xs font-semibold uppercase tracking-widest mb-3">Legal</p>
          <h1 className="text-4xl md:text-5xl font-black mb-4">Privacy Policy</h1>
          <p className="text-white/40 text-sm mb-12">Last updated: May 20, 2026</p>

          <div className="space-y-10 text-white/70 text-sm leading-relaxed">
            <div>
              <h2 className="text-white font-bold text-base mb-3">1. Information We Collect</h2>
              <p className="mb-3">
                Pixory AI collects information you provide directly when creating an account, including your name, email address, studio name, and billing details. We also collect photographs, client data, contracts, and other business content you upload to our platform to provide our photography business management services.
              </p>
              <p>
                Automatically collected information includes IP addresses, browser type, device identifiers, and usage analytics through standard server logs and cookies. Payment information is processed securely through our payment partners and is never stored on our servers.
              </p>
            </div>

            <div>
              <h2 className="text-white font-bold text-base mb-3">2. How We Use Your Data</h2>
              <p className="mb-3">
                Your data is used exclusively to operate, maintain, and improve the Pixory AI platform. This includes delivering our core services — client management, contract generation, payment tracking, job workflow automation, digital invitations, and photo delivery — as well as providing customer support, sending service-related communications, and ensuring platform security.
              </p>
              <p className="mb-3">
                We analyze aggregated, anonymized usage patterns to improve our features and user experience. We do not sell, rent, or share your personal data or your clients&apos; data with third parties for their marketing purposes.
              </p>
            </div>

            <div>
              <h2 className="text-white font-bold text-base mb-3">3. Client Data &amp; Photographs</h2>
              <p className="mb-3">
                As a photography business platform, Pixory AI stores photographs, client contact information, contracts, and event details that you upload. You retain full ownership of all creative content. We act solely as a data processor — we do not claim any rights to your photographs, client lists, or business data.
              </p>
              <p>
                Client data is stored securely with encryption at rest and in transit. You control who has access through client portals and digital invitation links. We recommend configuring appropriate access permissions for each client and event.
              </p>
            </div>

            <div>
              <h2 className="text-white font-bold text-base mb-3">4. Data Retention</h2>
              <p className="mb-3">
                We retain your account data and uploaded content for as long as your account remains active. If you cancel your account, we provide a 30-day grace period during which you can export all your data — including photographs, contracts, client records, and payment history — before permanent deletion from our systems.
              </p>
              <p>
                Backup archives may retain residual copies for up to 90 days after account deletion for disaster recovery purposes. Payment transaction records are retained as required by applicable financial regulations.
              </p>
            </div>

            <div>
              <h2 className="text-white font-bold text-base mb-3">5. Security Measures</h2>
              <p className="mb-3">
                Pixory AI implements industry-standard security practices including TLS encryption for all data in transit, AES-256 encryption for data at rest, regular security audits, and strict access controls. Our infrastructure is hosted on secure cloud providers with SOC 2 compliance.
              </p>
              <p>
                We enforce two-factor authentication for administrative access and maintain comprehensive activity logs for all account actions. Security vulnerabilities can be reported to security@pixory.ai and will be addressed within 48 hours.
              </p>
            </div>

            <div>
              <h2 className="text-white font-bold text-base mb-3">6. Your Rights</h2>
              <p className="mb-3">
                You have the right to access, correct, export, or delete your personal data at any time through your account settings or by contacting our support team. For users in the European Economic Area, Pixory AI complies with GDPR requirements including the right to data portability and the right to be forgotten.
              </p>
              <p>
                California residents have additional rights under the CCPA, including the right to know what personal information is collected and the right to opt out of the sale of personal information — though we do not sell personal data in any case.
              </p>
            </div>

            <div>
              <h2 className="text-white font-bold text-base mb-3">7. Third-Party Services</h2>
              <p className="mb-3">
                Pixory AI may integrate with third-party services you choose to connect, including payment processors, email delivery services, cloud storage providers, and messaging platforms. Each integration is governed by that provider&apos;s own privacy policy. We only share the minimum data necessary for each integration to function.
              </p>
              <p>
                A current list of our subprocessors is available upon request. We notify all account holders at least 30 days before adding new subprocessors that handle personal data.
              </p>
            </div>

            <div>
              <h2 className="text-white font-bold text-base mb-3">8. Contact Us</h2>
              <p className="mb-3">
                For privacy-related inquiries, data access requests, or to exercise your rights, contact our Data Protection Officer at <a href="mailto:privacy@pixory.ai" className="text-[#C8A96E] hover:text-[#d4b87a] transition-colors">privacy@pixory.ai</a>. We aim to respond to all privacy requests within 72 hours.
              </p>
              <p>
                Mailing address: Pixory AI, 3500 South DuPont Highway, Dover, DE 19901, United States.
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="text-white/30 hover:text-white/60 text-xs transition-colors">← Back to Pixory AI</Link>
          <span className="text-white/20 text-xs">© 2025 Pixory AI. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}